/**
 * useLearningCenter Hook
 * 
 * Data fetching and tracking for the Learning Center feature.
 * 
 * =====================================================================
 * IMPORTANT: TABLE RESPONSIBILITIES
 * =====================================================================
 * 
 * technician_learning_history = UI convenience history
 *   - Powers "Recently Viewed" in Technician Passport
 *   - Quick lookup of last 50 clicked artifacts
 *   - NOT the source of truth for analytics
 *   - Keep this for backwards compatibility with existing UI
 *
 * training_effectiveness_events = SINGLE SOURCE OF TRUTH for analytics
 *   - All canonical events (impressed/opened/engaged/completed/abandoned/rated)
 *   - Used by process-training-signals, aggregation, creator metrics
 *   - Use useTrainingEvents hook for writing to this table
 * 
 * =====================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// BAT-A2: bound the `.in('label', jobNames)` clause size in the
// shop-jobs → catalog-jobs resolution path. Large shops with imported
// job catalogs (post-CSV-import) can have 200–500 distinct job names; in
// the worst case the unbounded IN clause + a sister `.in('category_key',
// jobCategories)` filter combine into a single PostgREST request whose
// URL or body crosses the limits. Chunking the primary dimension
// (jobNames) while keeping the secondary `.in('category_key', ...)`
// filter inside the per-chunk lambda preserves the intersection
// semantics — each chunk returns only catalog rows whose label is in
// THIS chunk AND whose category_key matches one of the (small) shop
// categories.
import { chunkInQuery } from '@/utils/chunkInQuery';
import { fetchAllPages } from '@/lib/catalog/catalogPagination';
import { catalogIdsForShopJobs } from '@/lib/catalog/shopJobCatalogResolution';
import { learningHistoryStages, type LearningSessionStageInput } from '@/lib/training/learningHistoryStages';
import type { 
  TrainingArtifact, 
  LearningHistoryEntry,
  LearningHistoryWithArtifact,
  TrainingArtifactWithDomains,
  LearningIntent,
  LearningSource,
  ArtifactScope
} from '@/types/training';

/**
 * Fetch recommended training artifacts for a technician
 * Supports server-side pagination via page + pageSize.
 * Returns { data, total } so callers can show "X of Y" counts.
 */
export function useRecommendedTraining(options?: {
  domains?: string[];
  scope?: ArtifactScope;
  page?: number;
  pageSize?: number;
}) {
  const { domains, scope, page = 1, pageSize = 100 } = options || {};
  const offset = (page - 1) * pageSize;

  return useQuery({
    queryKey: ['training-artifacts', 'recommended', { domains, scope, page, pageSize }],
    queryFn: async () => {
      // FIX-R116-HIGH-1a: removed `.order('confidence_level', desc)`.
      // confidence_level is a STRING column with values
      // `optibay_verified` / `community_validated` / `provisional`. PostgREST
      // sorts strings alphabetically, so DESC ordering produced
      // `provisional` (p, 112) FIRST, then `optibay_verified` (o, 111),
      // then `community_validated` (c, 99) LAST — the exact opposite of the
      // intended quality order. This surfaced the lowest-quality artifacts at
      // the top of the Learning Center browse view. Confidence-tier ranking
      // is correctly applied downstream in contextualTrainingScorer /
      // trainingRecommendationEngine as a multiplier; the DB-level ordering
      // was the broken layer. Falling back to created_at (recent first) +
      // id tiebreaker keeps pagination deterministic.
      let query = supabase
        .from('training_artifacts')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true })
        .range(offset, offset + pageSize - 1);

      if (scope) {
        query = query.eq('artifact_scope', scope);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      let artifacts = data as TrainingArtifact[];
      let total = count ?? 0;

      // If domains specified, filter by domain mappings (client-side on current page)
      if (domains && domains.length > 0 && artifacts && artifacts.length > 0) {
        const artifactIds = artifacts.map(a => a.id);
        const { data: mappings } = await supabase
          .from('training_artifact_domain_map')
          .select('artifact_id, domain_key, is_primary')
          .in('artifact_id', artifactIds)
          .in('domain_key', domains);

        if (mappings) {
          const matchedIds = new Set(mappings.map(m => m.artifact_id));
          artifacts = artifacts.filter(a => matchedIds.has(a.id));
          total = artifacts.length; // approximate for domain-filtered views
        }
      }

      return { data: artifacts, total };
    },
  });
}

/**
 * Fetch training artifacts for a specific job category/domain
 * Used for ticket-level contextual learning
 * 
 * Priority order:
 * 1. Job-specific mappings (resolve shop job IDs → jobs_catalog → training) - highest priority
 * 2. Domain/category mappings (existing behavior) - fallback
 * 
 * Note: jobIds are from the shop's `jobs` table, NOT jobs_catalog.
 * We resolve them by matching name + category to jobs_catalog entries.
 */
export function useJobTraining(jobCategory: string | null, options?: {
  scope?: ArtifactScope;
  limit?: number;
  jobIds?: string[]; // Job UUIDs from shop's jobs table (NOT jobs_catalog)
}) {
  const { scope = 'job_specific', limit = 10, jobIds = [] } = options || {};

  return useQuery({
    queryKey: ['training-artifacts', 'job', jobCategory, { scope, jobIds }],
    queryFn: async () => {
      // Priority 1: Job-specific mapping (resolve shop jobs → catalog jobs → training)
      if (jobIds.length > 0) {
        // Step 1: Fetch shop jobs to get name + category
        const { data: shopJobs, error: shopJobsError } = await supabase
          .from('jobs')
          .select('id, name, category, catalog_job_id')
          .in('id', jobIds);

        if (shopJobsError) throw shopJobsError;

        if (shopJobs && shopJobs.length > 0) {
          // Step 2: Look up matching jobs_catalog entries by label + category_key
          const jobNames = shopJobs.map(j => j.name);
          const jobCategories = [...new Set(shopJobs.map(j => j.category))];

          // BAT-A2: chunk on jobNames (primary dimension); the `.in(
          // 'category_key', jobCategories)` filter remains inside the
          // per-chunk lambda so intersection semantics are preserved.
          const catalogRes = await chunkInQuery<
            string,
            { id: string; label: string; category_key: string }
          >(jobNames, (chunk) =>
            supabase
              .from('jobs_catalog')
              .select('id, label, category_key')
              .in('label', chunk)
              .in('category_key', jobCategories),
          );
          if (catalogRes.error) throw catalogRes.error;
          const catalogJobs = catalogRes.data;

          // A job's own catalog link wins (survives a catalog rename — FD-12);
          // name + category only for jobs without one.
          const catalogJobIds = catalogIdsForShopJobs(shopJobs, catalogJobs);
          if (catalogJobIds.length > 0) {
            // Step 3: Look up training artifacts for these catalog job IDs.
            // BAT-A2: chunk catalogJobIds. Bounded above by the shop's
            // job catalog size, but a 1:1 catalog match for every shop
            // job in a 500-job catalog walks the same unbounded path
            // pattern as Step 2.
            const jobMapRes = await chunkInQuery<
              string,
              { artifact_id: string; relevance_weight: number }
            >(catalogJobIds, (chunk) =>
              supabase
                .from('training_artifact_job_map')
                .select('artifact_id, relevance_weight')
                .in('job_id', chunk),
            );
            const jobMapError = jobMapRes.error;
            const jobMappings = jobMapRes.data;

            if (jobMapError) throw jobMapError;

            if (jobMappings && jobMappings.length > 0) {
              // Build weight map for sorting
              const weightMap = new Map<string, number>();
              jobMappings.forEach(m => {
                const existing = weightMap.get(m.artifact_id) || 0;
                weightMap.set(m.artifact_id, Math.max(existing, m.relevance_weight));
              });
              
              const artifactIds = Array.from(weightMap.keys());
              
              const { data, error } = await supabase
                .from('training_artifacts')
                .select('*')
                .in('id', artifactIds)
                .eq('is_active', true)
                .limit(limit);

              if (error) throw error;

              if (data && data.length > 0) {
                // Sort by relevance_weight DESC
                const sorted = (data as TrainingArtifact[]).sort((a, b) => {
                  const weightA = weightMap.get(a.id) || 0;
                  const weightB = weightMap.get(b.id) || 0;
                  return weightB - weightA;
                });
                return sorted;
              }
            }
          }
        }
      }

      // Priority 2: Domain/category mapping (existing behavior - fallback)
      if (!jobCategory) return [];

      // Get artifact IDs that match this domain
      const { data: mappings, error: mapError } = await supabase
        .from('training_artifact_domain_map')
        .select('artifact_id')
        .eq('domain_key', jobCategory);

      if (mapError) throw mapError;
      if (!mappings || mappings.length === 0) return [];

      const artifactIds = mappings.map(m => m.artifact_id);

      // FIX-R116-HIGH-1b: same alphabetical-sort bug as R116-HIGH-1a.
      // The DESC ordering on confidence_level (string) inverted intended
      // quality order — `provisional` first, `optibay_verified` middle,
      // `community_validated` last. Replaced with created_at DESC so the
      // ticket-level domain fallback returns recent content first; the
      // contextual scorer applies confidence as a multiplier downstream.
      const { data, error } = await supabase
        .from('training_artifacts')
        .select('*')
        .in('id', artifactIds)
        .eq('is_active', true)
        .eq('artifact_scope', scope)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as TrainingArtifact[];
    },
    enabled: !!jobCategory || jobIds.length > 0,
  });
}

/**
 * Fetch job mappings for a specific artifact (admin UI)
 */
export function useArtifactJobMappings(artifactId: string | null) {
  return useQuery({
    queryKey: ['training-artifact-job-mappings', artifactId],
    queryFn: async () => {
      if (!artifactId) return [];
      const { data, error } = await supabase
        .from('training_artifact_job_map')
        .select('*')
        .eq('artifact_id', artifactId);
      if (error) throw error;
      return data as { id: string; artifact_id: string; job_id: string; relevance_weight: number; created_at: string }[];
    },
    enabled: !!artifactId,
  });
}

/**
 * Fetch all jobs from jobs_catalog for job mapping dropdown
 * No limit - fetches entire catalog for complete search capability
 */
export function useJobsCatalog() {
  return useQuery({
    queryKey: ['jobs-catalog-for-training'],
    queryFn: async () => {
      // Every row, page by page: PostgREST silently caps one response at
      // max_rows (1000) — audit D AD-8.
      const { data, error } = await fetchAllPages<{ id: string; label: string; category_key: string; sub_menu: string }>(
        (from, to) =>
          supabase
            .from('jobs_catalog')
            .select('id, label, category_key, sub_menu', { count: 'exact' })
            .order('category_key')
            .order('label')
            .order('id')
            .range(from, to),
      );
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Core-100 R3 CORE-O-GAP-004: the person's learning history is read from the ONE durable, portable record —
 * `training_consumption_sessions` (bound to the technician profile, written for every launch by
 * useOpenTrainingWithSession). `technician_learning_history` was a second "convenience" copy of the same
 * personal data that nothing reconciled; it is no longer written or read (see useTrackLearningClick).
 */
export function launchSourceToLearningSource(launchSource: string | null | undefined): LearningSource {
  if (launchSource === 'ticket_training_tab' || launchSource === 'ticket_learning_panel'
      || launchSource === 'diagnostic_wizard' || launchSource === 'diagnostic_guidance') return 'ticket_context';
  if (launchSource === 'passport_recommendation_card') return 'recommendation';
  return 'learning_center';
}

interface ConsumptionSessionHistoryRow extends LearningSessionStageInput {
  id: string;
  technician_profile_id: string;
  artifact_id: string;
  opened_at: string | null;
  created_at: string;
  launch_source: string | null;
  ticket_id: string | null;
  metadata: { intent?: LearningIntent | null; job_category?: string | null } | null;
  artifact: LearningHistoryWithArtifact['artifact'];
}

export function toLearningHistoryEntry(row: ConsumptionSessionHistoryRow): LearningHistoryWithArtifact {
  const source = launchSourceToLearningSource(row.launch_source);
  return {
    id: row.id,
    technician_profile_id: row.technician_profile_id,
    artifact_id: row.artifact_id,
    clicked_at: row.opened_at ?? row.created_at,
    source,
    // The stated intent travels with the session; a ticket launch is job help by definition.
    intent: row.metadata?.intent ?? (source === 'ticket_context' ? 'job_help' : null),
    ticket_id: row.ticket_id,
    job_category: row.metadata?.job_category ?? null,
    artifact: row.artifact,
    // Core-100 R4 wave 2 CORE-O: the stages are kept apart and completion says how it was determined.
    stages: learningHistoryStages(row),
  };
}

/** The session columns the history needs to keep opened / engaged / progressed / completed apart. */
export const LEARNING_HISTORY_SESSION_COLUMNS =
  'id, technician_profile_id, artifact_id, opened_at, created_at, launch_source, ticket_id, metadata, engaged_at, progress_percent, completed_at, confirmed_at, completion_status, completion_confidence, manual_marked_complete_at, manager_verified_at, extension_evidence_summary';

/**
 * Fetch learning history for a technician
 */
export function useLearningHistory(technicianProfileId: string | null) {
  return useQuery({
    queryKey: ['learning-history', technicianProfileId],
    queryFn: async () => {
      if (!technicianProfileId) return [];

      const { data, error } = await supabase
        .from('training_consumption_sessions')
        .select(`${LEARNING_HISTORY_SESSION_COLUMNS}, artifact:training_artifacts(*)`)
        .eq('technician_profile_id', technicianProfileId)
        .order('opened_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (error) throw error;
      const entries = ((data ?? []) as unknown as ConsumptionSessionHistoryRow[]).map(toLearningHistoryEntry);
      // Core-100 R4 CORE-O: the catalogue hides retired training, so the embed is null for it. The person's own
      // history still names it and says it is no longer current (my_training_history_artifacts is self-only).
      if (entries.some((e) => !e.artifact)) {
        const { data: hist } = await supabase.rpc('my_training_history_artifacts' as never, {
          p_technician_profile_id: technicianProfileId,
        } as never);
        const byId = new Map(((hist ?? []) as unknown as Array<{ artifact_id: string; title: string; provider_name: string | null; artifact_type: string | null; is_active: boolean }>)
          .map((a) => [a.artifact_id, a]));
        return entries.map((e) => {
          const h = byId.get(e.artifact_id);
          if (e.artifact || !h) return { ...e, artifact_is_current: e.artifact ? true : null };
          return {
            ...e,
            artifact: { id: h.artifact_id, title: h.title, provider_name: h.provider_name, artifact_type: h.artifact_type } as unknown as LearningHistoryWithArtifact['artifact'],
            artifact_is_current: h.is_active,
          };
        });
      }
      return entries.map((e) => ({ ...e, artifact_is_current: true }));
    },
    enabled: !!technicianProfileId,
  });
}

/**
 * A technician clicked a training artifact.
 *
 * Core-100 R3 CORE-O-GAP-004: this used to INSERT a second, unreconciled copy of the launch into
 * `technician_learning_history`. Every launch is already recorded durably by useOpenTrainingWithSession
 * (`training_consumption_sessions`, with the stated intent in its metadata), so this hook no longer writes
 * anything: it only refreshes the person's history view, which reads the durable record.
 *
 * @deprecated For new code, launch through useOpenTrainingWithSession (it records the session).
 */
export function useTrackLearningClick() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_params: {
      technicianProfileId: string;
      artifactId: string;
      source: LearningSource;
      intent?: LearningIntent;
      ticketId?: string;
      jobCategory?: string;
    }) => null,
    onSuccess: (_, variables) => {
      // Invalidate learning history cache
      queryClient.invalidateQueries({
        queryKey: ['learning-history', variables.technicianProfileId],
      });
    },
  });
}

/**
 * Fetch job mapping counts for artifacts (admin UI)
 */
export function useArtifactJobMappingCounts() {
  return useQuery({
    queryKey: ['training-artifact-job-mapping-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifact_job_map')
        .select('artifact_id');
      
      if (error) throw error;
      
      // Count occurrences per artifact
      const counts = new Map<string, number>();
      data?.forEach(row => {
        counts.set(row.artifact_id, (counts.get(row.artifact_id) || 0) + 1);
      });
      
      return counts;
    },
  });
}

/**
 * Fetch vehicle fitment counts for artifacts (admin UI)
 */
export function useArtifactFitmentCounts() {
  return useQuery({
    queryKey: ['training-artifact-fitment-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifact_fitment')
        .select('artifact_id');
      
      if (error) throw error;
      
      // Count occurrences per artifact
      const counts = new Map<string, number>();
      data?.forEach(row => {
        counts.set(row.artifact_id, (counts.get(row.artifact_id) || 0) + 1);
      });
      
      return counts;
    },
  });
}

/**
 * Fetch artifacts with their domain mappings
 */
export function useArtifactsWithDomains(artifactIds?: string[]) {
  return useQuery({
    queryKey: ['training-artifacts', 'with-domains', artifactIds],
    queryFn: async () => {
      let artifactQuery = supabase
        .from('training_artifacts')
        .select('*')
        .eq('is_active', true);

      if (artifactIds && artifactIds.length > 0) {
        artifactQuery = artifactQuery.in('id', artifactIds);
      }

      const { data: artifacts, error: artifactError } = await artifactQuery;
      if (artifactError) throw artifactError;
      if (!artifacts || artifacts.length === 0) return [];

      const ids = artifacts.map(a => a.id);
      const { data: mappings, error: mapError } = await supabase
        .from('training_artifact_domain_map')
        .select('*')
        .in('artifact_id', ids);

      if (mapError) throw mapError;

      // Combine artifacts with their domain mappings
      return artifacts.map(artifact => {
        const artifactMappings = mappings?.filter(m => m.artifact_id === artifact.id) || [];
        const primaryMapping = artifactMappings.find(m => m.is_primary);
        
        return {
          ...artifact,
          domains: artifactMappings,
          primary_domain: primaryMapping?.domain_key,
        } as TrainingArtifactWithDomains;
      });
    },
  });
}

/**
 * Fetch fitment data for a specific artifact (admin UI)
 */
export function useArtifactFitment(artifactId: string | null) {
  return useQuery({
    queryKey: ['artifact-fitment', artifactId],
    queryFn: async () => {
      if (!artifactId) return [];
      const { data, error } = await supabase
        .from('training_artifact_fitment')
        .select('*')
        .eq('artifact_id', artifactId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as { 
        id: string; 
        artifact_id: string; 
        year: number | null;
        make: string | null;
        model: string | null;
        engine: string | null;
        confidence: string;
        boost: number;
        created_at: string; 
        updated_at: string;
      }[];
    },
    enabled: !!artifactId,
  });
}

/**
 * Fetch fitment data for multiple artifacts (recommendation engine)
 */
export function useFitmentForArtifacts(artifactIds: string[]) {
  return useQuery({
    queryKey: ['fitment-for-artifacts', artifactIds],
    queryFn: async () => {
      if (!artifactIds.length) return [];
      const { data, error } = await supabase
        .from('training_artifact_fitment')
        .select('*')
        .in('artifact_id', artifactIds);
      if (error) throw error;
      return data as {
        id: string;
        artifact_id: string;
        year: number | null;
        make: string | null;
        model: string | null;
        engine: string | null;
        confidence: string;
        boost: number;
      }[];
    },
    enabled: artifactIds.length > 0,
  });
}

/**
 * Fetch job adjacency map for given job IDs
 */
export function useJobAdjacencyMap(jobIds: string[]) {
  return useQuery({
    queryKey: ['job-adjacency', jobIds],
    queryFn: async () => {
      if (!jobIds.length) return [];
      const { data, error } = await supabase
        .from('job_adjacency_map')
        .select('source_job_id, related_job_id, boost')
        .in('source_job_id', jobIds);
      if (error) throw error;
      return data as {
        source_job_id: string;
        related_job_id: string;
        boost: number;
      }[];
    },
    enabled: jobIds.length > 0,
  });
}

/**
 * Fetch which artifacts have transcripts (admin UI)
 */
export function useArtifactTranscriptCounts() {
  return useQuery({
    queryKey: ['artifact-transcript-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_artifact_transcripts')
        .select('artifact_id');
      
      if (error) throw error;
      
      const transcriptMap = new Map<string, boolean>();
      data?.forEach(row => {
        transcriptMap.set(row.artifact_id, true);
      });
      
      return transcriptMap;
    },
  });
}
